import React, { ForwardedRef, forwardRef, useEffect } from 'react';
import {
  OverlayTriggerState,
  Popover as AriaPopover,
  PopoverProps as RACPopoverProps,
  OverlayTriggerStateContext,
} from 'react-aria-components';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
} from '../../utils/useSapphireStyleProps';
import clsx from 'clsx';
import popoverStyles from '@danske/sapphire-css/components/popover/popover.module.css';
import { AriaPositionPropsKeys, GlobalDomAttributes } from '../../utils/types';
import { FocusScope } from '@react-aria/focus';
import { ariaHideOutside } from '@react-aria/overlays';
import { useObjectRef } from '@react-aria/utils';

export interface PopoverProps
  extends Pick<
      RACPopoverProps,
      | 'triggerRef'
      | 'isNonModal'
      | 'isKeyboardDismissDisabled'
      | 'shouldCloseOnInteractOutside'
      | AriaPositionPropsKeys
    >,
    SapphireStyleProps,
    GlobalDomAttributes {
  state?: OverlayTriggerState;
  children: React.ReactNode;
  /**
   * Whether to include the default popover padding.
   * @default false
   */
  padded?: boolean;
  /**
   * Whether it has a predefined size constraint. Useful when the popover is
   * used as a dropdown of selects, menus etc.
   * @default false
   */
  defaultMaxWidth?: boolean;
}

/**
 * Helper component intended for internal use within Sapphire in components
 * like Select and Menu.
 *
 * *Popovers* are containers used to display transient content such as menus,
 * options, additional actions etc.
 *
 * @internal
 */
export const Popover = forwardRef(function Popover(
  {
    children,
    padded,
    defaultMaxWidth,
    maxHeight,
    state,
    ...props
  }: PopoverProps,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  const ref = useObjectRef(forwardedRef);
  const { styleProps } = useSapphireStyleProps({ ...props, maxHeight });

  /*
   * We need this effect to fix an issue in react-aria's "usePopover". The
   * issue is that a popover which is not a modal (see prop "isNonModal") will
   * get aria-hidden if opened in an existing modal. See more here
   * https://github.com/adobe/react-spectrum/issues/4213
   *
   * This same hack is present in react-aria's useComboBox.
   * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/combobox/src/useComboBox.ts#L300-L304
   */
  useEffect(() => {
    if (state && state.isOpen && props.isNonModal) {
      // input to ariaHideOutside is what NOT to hide (and their ancestors).
      return ariaHideOutside([
        props.triggerRef?.current as Element,
        ref?.current as Element,
      ]);
    }
  }, [state?.isOpen, props.isNonModal, ref, props.triggerRef]);

  const popover = (
    <AriaPopover
      {...props}
      ref={ref}
      style={styleProps.style}
      className={clsx(styleProps.className, popoverStyles['sapphire-popover'], {
        [popoverStyles['sapphire-popover--padded']]: padded,
        [popoverStyles['sapphire-popover--max-width']]: defaultMaxWidth,
      })}
    >
      <div
        className={popoverStyles['sapphire-popover__content']}
        role="presentation"
      >
        {/*
         * TODO UC-5016
         * The FocusScope traps focus within the popover when it's open, but it's not the best case scenario.
         * It was decided in react-aria that focus should not be trapped if the popover has role="dialog",
         * but some of our components (DateRangeField for example) use dialog props when rendered.
         * We need to investigate this further, after understanding why they decided that.
         */}
        <FocusScope
          restoreFocus
          autoFocus={!props.isNonModal}
          contain={!props.isNonModal}
        >
          {children}
        </FocusScope>
      </div>
    </AriaPopover>
  );

  if (state) {
    return (
      <OverlayTriggerStateContext.Provider value={state}>
        {popover}
      </OverlayTriggerStateContext.Provider>
    );
  }

  return popover;
});
